import React from 'react'
// libs
import { useSnackbar } from 'notistack'
// hooks
import { useShowModal } from 'hooks/index'
// components
import { VerticalMenu } from 'components/UI'
// context
import { LinkingModeContext } from 'containers/Plan/Edit/layers/API/Linking'
import { ObjectivesListAPIContext } from 'containers/ObjectivesList/layers/API'
import { ReviewDrawerContext } from 'context/ReviewDrawerContext'
import { ObjectivesListTreeBLContext } from 'containers/ObjectivesList/layers/BL/Tree'
// views
import { AddIcon, CenteredMenuItem, LinkIcon, NodeTitleWrapper, RemoveIcon } from 'themes/views/objectivesList'
// constants
import { ENTITIES, Modals } from 'constants/index'
// types
import { IPlanTypeLevelOption } from 'types/graphql'
// utils
import get from 'lodash/get'
import { getSnackbarCloseButton } from 'providers/SnackbarProvider/utils'

// types
interface IIncomingProps {
  node: any
  tree: any
  canLink: boolean
  isActive: boolean
  nodeTitle: string
  isObjective: boolean
  handleKPIAdd: () => void
  handleActionAdd: () => void
  handleObjectiveAdd: (event: React.MouseEvent, typeOption?: IPlanTypeLevelOption) => void
  nextLevelDescription: string
  shouldShowRemoveButton: boolean
  shouldShowAddKPIButton: boolean
  shouldShowAddActionButton: boolean
  shouldShowAddObjectiveButton: boolean
}

const NodeMenu: React.FC<IIncomingProps> = ({
  node,
  tree,
  canLink,
  isActive,
  nodeTitle,
  isObjective,
  handleKPIAdd,
  handleActionAdd,
  handleObjectiveAdd,
  nextLevelDescription,
  shouldShowAddKPIButton,
  shouldShowRemoveButton,
  shouldShowAddActionButton,
  shouldShowAddObjectiveButton
}) => {
  // context
  const { isLinkingMode, linkingSource } = React.useContext(LinkingModeContext)
  const { deletePlanItem } = React.useContext(ObjectivesListAPIContext)
  const { debouncedSynchronizeTree } = React.useContext(ObjectivesListTreeBLContext)
  const isReview = React.useContext(ReviewDrawerContext)

  // redux
  const { showModal: showConfirmUnlinkModal } = useShowModal({
    modalType: Modals.CONFIRM_UNLINK_PLAN_ITEM_MODAL
  })

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  // constants
  const isNodeReferenced: boolean = node.referenced
  const isNodeHaveRealChildren: boolean = get(node, 'children', []).some(({ referenced }) => !referenced)

  const isNodeLinked: boolean =
    isLinkingMode &&
    node.linkingItems.destinationLinkingItems.some(
      (linkedItem) =>
        linkedItem[
          {
            [ENTITIES.PLAN_OBJECTIVE]: 'sourcePlanObjectiveId',
            [ENTITIES.PLAN_ACTION]: 'sourcePlanActionId',
            [ENTITIES.PLAN_KPI]: 'sourcePlanKPIId'
          }[linkingSource.entityType as any]
        ] === linkingSource.id
    )

  const shouldShowLinkButton: boolean = isLinkingMode && !isNodeLinked && linkingSource.id !== node.entityId && canLink

  const levelOptions: IPlanTypeLevelOption[] = node.nextLevel?.typeOptions || []

  // methods
  const removeReferencedPlanItem = async (event: React.MouseEvent) => {
    event.stopPropagation()

    if (isNodeReferenced && isNodeHaveRealChildren) {
      return enqueueSnackbar('Please remove subitems first!', {
        variant: 'warning',
        action: (key) => getSnackbarCloseButton(key, closeSnackbar)
      })
    }

    await deletePlanItem(node.id)

    const treeFilter = ({ id }) => id !== node.id

    debouncedSynchronizeTree(tree, treeFilter)
  }

  const linkPlanItem = (event: React.MouseEvent) => {
    event.stopPropagation()
    node.linkItem()
  }

  const unlinkPlanItem = (event: React.MouseEvent) => {
    event.stopPropagation()
    showConfirmUnlinkModal({ handleConfirm: node.unlinkItem, title: nodeTitle })
  }

  return (
    <VerticalMenu
      disableAutoFocus
      trigger={
        <NodeTitleWrapper
          $isActive={isActive}
          $isObjective={isObjective}
          $isReview={isReview}
          $isNodeReferenced={isNodeReferenced}>
          {nodeTitle}
        </NodeTitleWrapper>
      }>
      {shouldShowAddObjectiveButton && (
        <>
          <CenteredMenuItem onClick={handleObjectiveAdd}>
            <AddIcon /> Add {nextLevelDescription}
          </CenteredMenuItem>
          {levelOptions.map((typeOption) => (
            <CenteredMenuItem key={typeOption.id} onClick={(event) => handleObjectiveAdd(event, typeOption)}>
              <AddIcon /> Add {typeOption.label}
            </CenteredMenuItem>
          ))}
        </>
      )}
      {shouldShowAddActionButton && (
        <CenteredMenuItem onClick={handleActionAdd}>
          <AddIcon /> Add Action
        </CenteredMenuItem>
      )}
      {shouldShowAddKPIButton && (
        <CenteredMenuItem onClick={handleKPIAdd}>
          <AddIcon /> Add Indicator
        </CenteredMenuItem>
      )}
      {isNodeReferenced && shouldShowRemoveButton && (
        <CenteredMenuItem onClick={removeReferencedPlanItem}>
          <RemoveIcon /> Remove from sub plan
        </CenteredMenuItem>
      )}
      {shouldShowLinkButton && (
        <CenteredMenuItem $isLink onClick={linkPlanItem}>
          <LinkIcon /> Link
        </CenteredMenuItem>
      )}
      {isLinkingMode && isNodeLinked && (
        <CenteredMenuItem $isLink onClick={unlinkPlanItem}>
          <LinkIcon /> Unlink
        </CenteredMenuItem>
      )}
    </VerticalMenu>
  )
}

export default NodeMenu
